import {Badge, Button, Code, FormControl, FormLabel, Heading, Stack, Switch} from '@chakra-ui/react';
import React, {useEffect, useState} from 'react';
import {useVuplex} from '../../hooks/useVuplex';
import {sendDevConsoleRaceProps, sendDevConsoleRacePropsRequest} from '../../vuplex';
import _ from 'lodash';
import {NumberControl, StringControl} from './NumberControl';
import MultiSelect from '../../components/Multiselect';
import {jetpackClankers} from 'common';
import dayjs from 'dayjs';

const sendPropsDebounced = _.debounce((props) => {
  sendDevConsoleRaceProps(props);
}, 200);

const hiddenPropNames = [
  'arbitraryGhostsToPlayback',
  'racesToPlayback',
];

const getRaceGhostCaptureLabel = (capture) => {
  const createdOn = capture.createdOn
    ? dayjs(capture.createdOn).format('YYYY-MM-DD HH:mm')
    : 'Unknown date';
  const trackLabel = (_.isNil(capture.trackId) || capture.trackId < 0)
    ? capture.captureId
    : jetpackClankers.raceTracksById[capture.trackId]?.label || `Track ${capture.trackId}`;

  return [
    trackLabel,
    createdOn,
    // `${capture.snapshotCount} snapshots`,
    // capture.captureId,
  ].join(' | ');
};

const getGhostCapturesControl = (captures, ghostsToPlayback, title, onChange) => {
  const options = _.compact(
    _.orderBy(
      captures,
      [(capture) => (capture.createdOn ? dayjs(capture.createdOn).valueOf() : 0)],
      ['desc']
    ).map((capture) => (
      capture.captureId && {
        label: getRaceGhostCaptureLabel(capture),
        value: capture.captureId,
      }
    ))
  );
  const selectedCaptureIds = Array.isArray(ghostsToPlayback)
    ? _.compact(ghostsToPlayback.map((ghost) => (
      typeof ghost === 'string'
        ? ghost
        : ghost.captureId
    )))
    : [];

  return (
    <MultiSelect
      title={title}
      options={options}
      values={selectedCaptureIds}
      onChange={onChange}
    />
  );
};

const getRaceGhostCapturesControl = (captures, raceProps, onPropChange) => {
  const onChange = (captureIds) => {
    const racesToPlayback = _.compact(captureIds.map((captureId) => {
      const capture = raceProps.raceGhostCaptures?.find((raceGhostCapture) => (
        raceGhostCapture.captureId === captureId
      ));

      return capture && {
        trackId: capture.trackId,
        captureId: capture.captureId,
      };
    }));

    onPropChange('racesToPlayback')(racesToPlayback);
  };

  return getGhostCapturesControl(
    captures,
    raceProps.racesToPlayback,
    'Race ghost captures',
    onChange
  );
};

const getArbitraryGhostCapturesControl = (captures, raceProps, onPropChange) => {
  const onChange = (captureIds) => {
    const arbitraryGhostsToPlayback = _.compact(captureIds.map((captureId) => {
      const capture = raceProps.arbitraryGhostCaptures?.find((arbitraryGhostCapture) => (
        arbitraryGhostCapture.captureId === captureId
      ));

      return capture && {
        captureId: capture.captureId,
      };
    }));

    onPropChange('arbitraryGhostsToPlayback')(arbitraryGhostsToPlayback);
  };

  return getGhostCapturesControl(
    captures,
    raceProps.arbitraryGhostsToPlayback,
    'Arbitrary ghost captures',
    onChange
  );
};

const getRecordingStatusControl = (isRecording) => (
  <Badge colorScheme={isRecording ? 'red' : 'gray'}>
    {isRecording ? 'Recording' : 'Not recording'}
  </Badge>
);

const getControl = (name, value, onPropChange, raceProps) => {
  const isMomentarySwitch = typeof value === 'boolean' && name.endsWith('MomentarySwitch');
  if (isMomentarySwitch) {
    return (
      <Button onClick={() => onPropChange(name)(true)}>
        {name.replace(/MomentarySwitch$/, '')}
      </Button>
    );
  }

  const controlByName = {
    isRecordingArbitraryGhost: getRecordingStatusControl(value),
    isRecordingCurrentRace: getRecordingStatusControl(value),
    raceGhostCaptures: Array.isArray(value)
      ? getRaceGhostCapturesControl(value, raceProps, onPropChange)
      : null,
    arbitraryGhostCaptures: Array.isArray(value)
      ? getArbitraryGhostCapturesControl(value, raceProps, onPropChange)
      : null,
  };

  const controlByType = {
    string: (
      <StringControl
        name={name}
        value={value}
        onChange={onPropChange(name)}
      />
    ),
    number: (
      <NumberControl
        name={name}
        value={value}
        onChange={onPropChange(name)}
      />
    ),
    boolean: (
      <Switch
        isChecked={value}
        onChange={(e) => onPropChange(name)(e.target.checked)}
      />
    ),
  };

  return controlByName[name] || controlByType[typeof value];
};


/*
* example raceProps: {
    "ghostRecorderEnabled": true,
    "isRecordingCurrentRace": false,
    "racesToPlayback": [],
    "raceGhostCaptures": [
        {
            "trackId": 0,
            "captureId": "2f23a7e6a51644698621317d12b10de1",
            "createdOn": "2026-05-20T18:50:17.5028945Z",
            "snapshotCount": 2981
        }
    ],
    "raceJoinMomentarySwitch": false,
    "raceLeaveMomentarySwitch": false,
    "raceStartMomentarySwitch": false,
    "raceQuitMomentarySwitch": false,
    "teleportToStartMomentarySwitch": false,
    "checkpointsTotal": 51,
    "lapsTotal": 1
}*/


const Race = () => {
  const [raceProps, setRaceProps] = useState({});
  const [raceState, setRaceState] = useState({});

  useVuplex({
    raceProps: (props) => {
      console.log('raceProps', props);
      setRaceProps(props);
    },
    raceState: (state) => {
      setRaceState(state);
    }
  });

  useEffect(() => {
    sendDevConsoleRacePropsRequest();
  }, []);

  const onPropChange = (name) => (nextVal) => {
    const next = {
      ...raceProps,
      [name]: nextVal,
    };

    setRaceProps(next);
    sendPropsDebounced(next);
  };

  return (
    <Stack>
      <Heading>Raceypoo</Heading>
      <Button
        onClick={sendDevConsoleRacePropsRequest}
      >
        Get Current Race Props
      </Button>
      {_.map(raceProps, (value, name) => {
        if (_.includes(hiddenPropNames, name)) {
          return null;
        }

        return (
          <FormControl key={name} border="1px solid" borderRadius="2xl" p={2}>
            <FormLabel>{name}</FormLabel>
            {getControl(
              name,
              value,
              onPropChange,
              raceProps
            )}
          </FormControl>
        );
      })}
      <Code>
        {JSON.stringify(raceState, null, 2)}
      </Code>
    </Stack>);
}

export default Race;
